// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Beeline — the world map view. Canvas 2D, a fixed full-world equirectangular
// projection (no pan/zoom: the map is a heat overview, the guess list carries the
// precise numbers). Guessed countries flood with a warmth colour; the reveal
// draws a beeline arc from the last guess to the answer.
//
// rAF drives ANIMATION ONLY (flash decay, reveal pulse, particles) and stops
// itself when idle — game logic never touches it. Guarded against 0-size measures
// so a transient 0×0 rect can't produce NaN coordinates.

import { ALL, type Country } from './data';
import type { Guess } from './game';
import { PALETTE, heatColor } from './palette';

export interface Scene {
  guesses: Guess[];
  mysteryId: string | null;
  /** Reveal the answer (solved or round over). */
  reveal: boolean;
  reducedMotion: boolean;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  col: string;
}

export interface MapView {
  resize(): void;
  render(scene: Scene): void;
  destroy(): void;
}

export function createMapView(canvas: HTMLCanvasElement): MapView {
  const ctx = canvas.getContext('2d')!;
  let dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
  let cssW = 0;
  let cssH = 0;
  let scale = 1;
  let ox = 0;
  let oy = 0;

  const paths = new Map<string, Path2D>();
  let scene: Scene = { guesses: [], mysteryId: null, reveal: false, reducedMotion: false };
  let particles: Particle[] = [];
  let flashId: string | null = null;
  let flashAt = 0;
  let revealAt = 0;
  let raf = 0;
  let lastRevealFlag = false;

  const proj = (lon: number, lat: number): [number, number] => [
    ox + (lon + 180) * scale,
    oy + (90 - lat) * scale,
  ];

  function buildPaths(): void {
    paths.clear();
    for (const c of ALL) {
      const p = new Path2D();
      for (const ring of c.rings) {
        for (let i = 0; i < ring.length; i += 2) {
          const [x, y] = proj(ring[i], ring[i + 1]);
          if (i === 0) p.moveTo(x, y);
          else p.lineTo(x, y);
        }
        p.closePath();
      }
      paths.set(c.id, p);
    }
  }

  function resize(): void {
    const rect = canvas.getBoundingClientRect();
    const w = rect.width || canvas.clientWidth;
    const h = rect.height || canvas.clientHeight;
    // Guard against a transient 0-size measurement — computing a scale from 0×0
    // yields NaN coordinates and a blank map. Ignore it; the next frame retries.
    if (w < 2 || h < 2) return;
    dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
    cssW = w;
    cssH = h;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    scale = Math.min(w / 360, h / 180);
    const mapW = 360 * scale;
    const mapH = 180 * scale;
    ox = (w - mapW) / 2;
    oy = (h - mapH) / 2;
    buildPaths();
    frame();
  }

  function centroidPx(id: string): [number, number] | null {
    const c = ALL.find((k) => k.id === id);
    if (!c) return null;
    return proj(c.c[0], c.c[1]);
  }

  function burst(id: string, col: string): void {
    if (scene.reducedMotion) return;
    const p = centroidPx(id);
    if (!p) return;
    for (let i = 0; i < 22; i++) {
      const a = (i / 22) * Math.PI * 2;
      const sp = 0.6 + Math.random() * 1.6;
      particles.push({
        x: p[0],
        y: p[1],
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        life: 0,
        max: 500 + Math.random() * 400,
        col,
      });
    }
  }

  let prevTs = 0;
  function needsFrame(): boolean {
    if (particles.length) return true;
    if (flashId && performance.now() - flashAt < 550) return true;
    if (scene.reveal && !scene.reducedMotion && performance.now() - revealAt < 2400) return true;
    return false;
  }

  function loop(ts: number): void {
    const dt = prevTs ? ts - prevTs : 16;
    prevTs = ts;
    particles = particles.filter((p) => (p.life += dt) < p.max);
    for (const p of particles) {
      p.x += p.vx * dt * 0.06;
      p.y += p.vy * dt * 0.06;
      p.vy += dt * 0.002;
    }
    draw();
    raf = needsFrame() ? requestAnimationFrame(loop) : 0;
  }

  function frame(): void {
    if (!raf) {
      prevTs = 0;
      raf = requestAnimationFrame(loop);
    }
  }

  function draw(): void {
    if (cssW < 2) return;
    const guessed = new Map(scene.guesses.map((g) => [g.id, g]));

    ctx.fillStyle = PALETTE.sea;
    ctx.fillRect(0, 0, cssW, cssH);

    // Fills.
    for (const c of ALL) {
      const path = paths.get(c.id)!;
      const g = guessed.get(c.id);
      if (c.id === scene.mysteryId && scene.reveal) ctx.fillStyle = PALETTE.solved;
      else if (g) ctx.fillStyle = heatColor(g.pct, g.correct);
      else ctx.fillStyle = PALETTE.land;
      ctx.fill(path);
    }

    // Borders.
    ctx.lineJoin = 'round';
    ctx.lineWidth = Math.max(0.5, scale * 0.35);
    ctx.strokeStyle = PALETTE.landBorder;
    for (const c of ALL) {
      if (guessed.has(c.id) || (c.id === scene.mysteryId && scene.reveal)) continue;
      ctx.stroke(paths.get(c.id)!);
    }
    ctx.lineWidth = Math.max(0.7, scale * 0.5);
    ctx.strokeStyle = PALETTE.ink;
    for (const g of scene.guesses) {
      const p = paths.get(g.id);
      if (p) ctx.stroke(p);
    }

    // Reveal: glowing ring on the answer + beeline arc from the last guess.
    if (scene.reveal && scene.mysteryId) {
      const m = centroidPx(scene.mysteryId);
      if (m) {
        const t = Math.min(1, (performance.now() - revealAt) / 500);
        const last = scene.guesses.filter((x) => !x.correct).at(-1);
        if (last) {
          const from = centroidPx(last.id);
          if (from) drawArc(from, m, scene.reducedMotion ? 1 : t);
        }
        const pulse = scene.reducedMotion ? 0 : 0.5 + 0.5 * Math.sin(performance.now() / 240);
        ctx.beginPath();
        ctx.arc(m[0], m[1], 7 + scale * 0.6 + pulse * 4, 0, Math.PI * 2);
        ctx.strokeStyle = PALETTE.solved;
        ctx.lineWidth = 2.5;
        ctx.globalAlpha = 0.85;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    }

    // Flash the just-guessed country.
    if (flashId) {
      const age = performance.now() - flashAt;
      if (age < 550) {
        const p = paths.get(flashId);
        if (p) {
          ctx.save();
          ctx.globalAlpha = (1 - age / 550) * 0.9;
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          ctx.stroke(p);
          ctx.restore();
        }
      }
    }

    // Particles.
    for (const p of particles) {
      ctx.globalAlpha = Math.max(0, 1 - p.life / p.max);
      ctx.fillStyle = p.col;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 2.2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  function drawArc(a: [number, number], b: [number, number], t: number): void {
    const mx = (a[0] + b[0]) / 2;
    const my = (a[1] + b[1]) / 2 - Math.hypot(b[0] - a[0], b[1] - a[1]) * 0.18;
    ctx.beginPath();
    ctx.moveTo(a[0], a[1]);
    // Sample the quadratic up to t so it "draws in".
    const steps = 32;
    for (let i = 1; i <= steps * t; i++) {
      const u = i / steps;
      const iu = 1 - u;
      ctx.lineTo(
        iu * iu * a[0] + 2 * iu * u * mx + u * u * b[0],
        iu * iu * a[1] + 2 * iu * u * my + u * u * b[1],
      );
    }
    ctx.strokeStyle = PALETTE.accent;
    ctx.lineWidth = 2.5;
    ctx.setLineDash([2, 4]);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = PALETTE.accent;
    ctx.beginPath();
    ctx.arc(a[0], a[1], 3.4, 0, Math.PI * 2);
    ctx.fill();
  }

  function render(next: Scene): void {
    const prevCount = scene.guesses.length;
    scene = next;
    if (next.guesses.length > prevCount) {
      const g = next.guesses.at(-1)!;
      flashId = g.id;
      flashAt = performance.now();
      if (!next.reducedMotion) burst(g.id, g.correct ? PALETTE.solved : heatColor(g.pct));
    }
    if (next.reveal && !lastRevealFlag) {
      revealAt = performance.now();
      if (next.mysteryId && next.guesses.some((x) => x.correct)) {
        burst(next.mysteryId, PALETTE.solved);
      }
    }
    lastRevealFlag = next.reveal;
    if (needsFrame()) frame();
    else draw();
  }

  const onWin = (): void => resize();
  window.addEventListener('resize', onWin);
  window.addEventListener('orientationchange', onWin);
  resize();

  return {
    resize,
    render,
    destroy() {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      window.removeEventListener('resize', onWin);
      window.removeEventListener('orientationchange', onWin);
    },
  };
}

/** Draw a country's silhouette (outline) into a small square canvas, fit to box. */
export function drawSilhouette(canvas: HTMLCanvasElement, c: Country): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
  const rect = canvas.getBoundingClientRect();
  const w = rect.width || 120;
  const h = rect.height || 120;
  canvas.width = Math.round(w * dpr);
  canvas.height = Math.round(h * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  // Bounding box of all rings.
  let minx = Infinity;
  let miny = Infinity;
  let maxx = -Infinity;
  let maxy = -Infinity;
  for (const ring of c.rings) {
    for (let i = 0; i < ring.length; i += 2) {
      minx = Math.min(minx, ring[i]);
      maxx = Math.max(maxx, ring[i]);
      miny = Math.min(miny, ring[i + 1]);
      maxy = Math.max(maxy, ring[i + 1]);
    }
  }
  const pad = 10;
  const bw = Math.max(0.001, maxx - minx);
  const bh = Math.max(0.001, maxy - miny);
  const s = Math.min((w - pad * 2) / bw, (h - pad * 2) / bh);
  const px = (lon: number) => pad + (lon - minx) * s + (w - pad * 2 - bw * s) / 2;
  const py = (lat: number) => pad + (maxy - lat) * s + (h - pad * 2 - bh * s) / 2;

  ctx.beginPath();
  for (const ring of c.rings) {
    for (let i = 0; i < ring.length; i += 2) {
      const x = px(ring[i]);
      const y = py(ring[i + 1]);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
  }
  ctx.fillStyle = PALETTE.mild;
  ctx.fill();
  ctx.strokeStyle = PALETTE.ink;
  ctx.lineWidth = 1.5;
  ctx.lineJoin = 'round';
  ctx.stroke();
}
